/**
 * Agent-run references let a user pull another run's full context into a
 * prompt. In the composer the reference is compact, readable text —
 * `@[<title>](agent-run:<id>)` — so the chat shows a tidy token, not a wall of
 * context. Server-side, every marker is expanded into the referenced run's
 * transcript right before the prompt reaches the agent, so the agent receives
 * the full context while the displayed prompt stays compact.
 *
 * This module is the single source of truth for the marker format, shared by
 * the web composer (which builds markers) and the server (which parses and
 * expands them).
 */

import {
  splitContextRefBlocks,
  splitContextRefMarkers,
  type ContextRefSegment,
} from './context-reference.js';

// One marker: `@[` + readable label (no `]` or newline) + `](agent-run:<id>)`.
// The id charset covers prefixed ids (`agt_...`), uuids, and `:`-segmented ids.
// The label is length-bounded: an unbounded `[^\]\n]*` is a polynomial-ReDoS
// vector because every `@[` in the (user-controlled) prompt is a start anchor
// that scans to end-of-line, so overlapping starts cost O(n^2). A bound makes
// each scan constant work; 512 dwarfs any real sanitized run title.
const AGENT_RUN_REFERENCE = /@\[[^\]\n]{0,512}\]\(agent-run:([A-Za-z0-9_:.-]+)\)/g;

/** Strip characters that would break the marker's `[title](...)` framing. */
export function sanitizeAgentRunTitle(title: string): string {
  return title
    .replace(/[[\]()\r\n]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Build the composer token for a referenced agent run. */
export function buildAgentRunReference(id: string, title: string): string {
  const label = sanitizeAgentRunTitle(title) || 'agent run';
  return `@[${label}](agent-run:${id})`;
}

export interface AgentRunReference {
  /** The full matched marker text. */
  raw: string;
  /** The referenced agent run id. */
  id: string;
  /** Index of the marker in the source string. */
  index: number;
}

/** Find every agent-run reference marker in a string, in order. */
export function parseAgentRunReferences(text: string): AgentRunReference[] {
  const out: AgentRunReference[] = [];
  for (const match of text.matchAll(AGENT_RUN_REFERENCE)) {
    out.push({ raw: match[0], id: match[1], index: match.index ?? 0 });
  }
  return out;
}

/** Whether a string contains at least one agent-run reference marker. */
export function hasAgentRunReference(text: string): boolean {
  return parseAgentRunReferences(text).length > 0;
}

// The server replaces a marker with a delimited context block so the agent
// receives the referenced run's transcript inline. The web chat collapses the
// same block back into a badge, so this wrapper is the shared contract.
/** Wrap a referenced run's context body in the delimited block the chat knows. */
export function wrapAgentRunContext(id: string, title: string, body: string): string {
  return `<referenced-agent-run id="${id}" title=${JSON.stringify(title)}>\n${body}\n</referenced-agent-run>`;
}

export type AgentRunContextSegment =
  | { type: 'text'; value: string }
  | { type: 'reference'; id: string; title: string };

/**
 * A composer segment: plain text, an agent-run reference, or a plugin context
 * reference (`context-reference.ts`). The composer, the chat renderer and the
 * optimistic-echo normalizer all speak this union so every reference form
 * round-trips through one parser.
 */
export type ComposerSegment = AgentRunContextSegment | ContextRefSegment;

// The compact composer marker, capturing both the title and the id. The title
// is length-bounded for the same polynomial-ReDoS reason as AGENT_RUN_REFERENCE.
const AGENT_RUN_MARKER_CAPTURE = /@\[([^\]\n]{0,512})\]\(agent-run:([A-Za-z0-9_:.-]+)\)/g;

/**
 * Split text into plain-text segments and compact `@[title](agent-run:id)`
 * markers. Used to re-hydrate a saved prompt string back into composer chips.
 */
export function splitAgentRunMarkers(text: string): AgentRunContextSegment[] {
  const segments: AgentRunContextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(AGENT_RUN_MARKER_CAPTURE)) {
    const index = match.index ?? 0;
    if (index > cursor) segments.push({ type: 'text', value: text.slice(cursor, index) });
    segments.push({ type: 'reference', id: match[2], title: match[1] });
    cursor = index + match[0].length;
  }
  if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });
  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
}

/**
 * Parse a stored prompt string into composer segments, recognizing BOTH the
 * compact markers (an unlaunched draft) AND the expanded blocks (a launched
 * run, e.g. "Launch again") — for agent-run references and plugin context
 * references alike. Either form collapses back into a single reference, so
 * seeding the composer re-hydrates chips for free.
 */
export function parseComposerReferences(text: string): ComposerSegment[] {
  const out: ComposerSegment[] = [];
  for (const block of splitAgentRunContextBlocks(text)) {
    if (block.type === 'reference') {
      out.push(block);
      continue;
    }
    for (const ctxBlock of splitContextRefBlocks(block.value)) {
      if (ctxBlock.type === 'context') {
        out.push(ctxBlock);
        continue;
      }
      for (const seg of splitAgentRunMarkers(ctxBlock.value)) {
        if (seg.type === 'reference') {
          out.push(seg);
          continue;
        }
        out.push(...splitContextRefMarkers(seg.value));
      }
    }
  }
  return out;
}

// A token no user text can contain, so a collapsed reference can never collide
// with surrounding prose. The id is stable across both reference forms.
const AGENT_RUN_REFERENCE_KEY = (id: string) => ` agent-run:${id} `;
const CONTEXT_REFERENCE_KEY = (kind: string, id: string) => ` ${kind}:${id} `;

/**
 * Collapse every reference — compact `@[title](kind:id)` marker OR expanded
 * block, agent-run and plugin context alike — down to a stable, content-free
 * token keyed on the referenced id, leaving surrounding text intact. Two
 * strings that reference the same things in the same places then compare equal
 * even when one side has been server-expanded into full context. Used to
 * reconcile an optimistic (compact) pending message against its expanded
 * stream echo.
 */
export function normalizeAgentRunReferences(text: string): string {
  return parseComposerReferences(text)
    .map((seg) => {
      if (seg.type === 'reference') return AGENT_RUN_REFERENCE_KEY(seg.id);
      if (seg.type === 'context') return CONTEXT_REFERENCE_KEY(seg.kind, seg.id);
      return seg.value;
    })
    .join('');
}

// Any opening or closing referenced-agent-run tag, used to find the *balanced*
// close of an outer block (a referenced run's transcript can itself contain
// nested blocks from its own prior expansions).
const REFERENCED_AGENT_RUN_TAG = /<(\/?)referenced-agent-run\b[^>]*>/g;
const REFERENCED_AGENT_RUN_OPEN_ATTRS = /id="([^"]*)"\s+title="((?:[^"\\]|\\.)*)"/;

/**
 * Split text into plain-text segments and referenced-agent-run blocks, so a
 * renderer can show each block as a badge instead of a wall of transcript.
 * Nesting-aware: matches each outer block to its balanced close. Returns a
 * single text segment when there are no blocks.
 */
export function splitAgentRunContextBlocks(text: string): AgentRunContextSegment[] {
  const segments: AgentRunContextSegment[] = [];
  let cursor = 0;
  const tag = new RegExp(REFERENCED_AGENT_RUN_TAG);
  let match: RegExpExecArray | null;
  while ((match = tag.exec(text)) !== null) {
    if (match[1]) continue; // a closing tag with no matching open we tracked
    const attrs = match[0].match(REFERENCED_AGENT_RUN_OPEN_ATTRS);
    if (!attrs) continue; // malformed opening tag
    // Find the balanced close, counting nested opens.
    const scan = new RegExp(REFERENCED_AGENT_RUN_TAG);
    scan.lastIndex = tag.lastIndex;
    let depth = 1;
    let closeEnd = -1;
    let inner: RegExpExecArray | null;
    while ((inner = scan.exec(text)) !== null) {
      depth += inner[1] ? -1 : 1;
      if (depth === 0) {
        closeEnd = scan.lastIndex;
        break;
      }
    }
    if (closeEnd === -1) continue; // unbalanced — leave as text
    if (match.index > cursor)
      segments.push({ type: 'text', value: text.slice(cursor, match.index) });
    let title = attrs[2];
    try {
      title = JSON.parse(`"${attrs[2]}"`) as string;
    } catch {
      // Leave the raw (escaped) title if it isn't valid JSON string content.
    }
    segments.push({ type: 'reference', id: attrs[1], title });
    cursor = closeEnd;
    tag.lastIndex = closeEnd; // resume after the whole (balanced) block
  }
  if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });
  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
}

/**
 * Replace every agent-run reference marker using `resolve(id)`. Each unique id
 * is resolved once. When `resolve` returns `null` (not found, or the caller is
 * not authorized to read it) the original marker is left untouched. Async so
 * the server can fetch transcripts.
 */
export async function expandAgentRunReferences(
  text: string,
  resolve: (id: string) => Promise<string | null>,
): Promise<string> {
  const refs = parseAgentRunReferences(text);
  if (refs.length === 0) return text;

  const resolved = new Map<string, string | null>();
  await Promise.all(
    [...new Set(refs.map((r) => r.id))].map(async (id) => {
      resolved.set(id, await resolve(id));
    }),
  );

  // Rebuild left-to-right so each splice keeps the remaining indices valid.
  let out = '';
  let cursor = 0;
  for (const ref of refs) {
    out += text.slice(cursor, ref.index);
    out += resolved.get(ref.id) ?? ref.raw;
    cursor = ref.index + ref.raw.length;
  }
  return out + text.slice(cursor);
}
